import React, { useMemo, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Space, Row, Col, Collapse, Typography } from 'antd';
import CollapsePanel from 'antd/lib/collapse/CollapsePanel';
import useForm from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import SaveButton from '../buttons/SaveButton';
import PageTitle from '../PageTitle';
import CancelButton from '../buttons/CancelButton';
import usePermissions from '../../hooks/usePermission';

const DURATION_TYPES = [
  { id: 'DAY' },
  { id: 'WEEK' },
  { id: 'MONTH' },
  { id: 'YEAR' },
  { id: 'CALENDAR_MONTH' },
  { id: 'CALENDAR_HALF_MONTH' },
  { id: 'CALENDAR_YEAR' },
  { id: 'CALENDAR_DAY' },
  { id: 'CALENDAR_WEEK' },
  { id: 'SCHOOL_YEAR' },
  { id: 'PENSIONER_YEAR' },
  { id: 'ACADEMIC_YEAR' },
];

const permissionsMap = {
  canEdit: ['SYS_SUPERUSER', 'TAXONOMIES_PERMISSION'],
};

const EditDocTypeForm = ({ docType, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const { canEdit } = usePermissions(permissionsMap);
  const maxNameChar = 60;
  const [activeKeys, setActiveKey] = useState(['name']);
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        localizedName: yup.object().shape({
          ro: yup
            .string()
            .required(t('errors.notEmpty'))
            .max(maxNameChar, t('errors.maxLength', { max: maxNameChar })),
          en: yup
            .string()
            .required(t('errors.notEmpty'))
            .max(maxNameChar, t('errors.maxLength', { max: maxNameChar })),
          de: yup
            .string()
            .required(t('errors.notEmpty'))
            .max(maxNameChar, t('errors.maxLength', { max: maxNameChar })),
        }),
        code: yup.string().required(t('errors.notEmpty')),
      }),
    [t],
  );

  const {
    value,
    handleSubmit,
    input,
    select,
    checkbox,
    setFormValue,
    setValue,
    errorMessages,
  } = useForm({
    initialValue: docType,
    schema: validationSchema,
    onSubmit,
    onCancel,
  });

  useEffect(() => {
    setFormValue(docType);
  }, [setFormValue, docType]);

  useEffect(() => {
    if (
      errorMessages &&
      Object.keys(errorMessages).some((e) =>
        ['name', 'nameEn', 'nameDe'].includes(e),
      )
    ) {
      setActiveKey(['name']);
    }
  }, [errorMessages]);

  const { deleted } = value;

  return (
    <>
      <form onSubmit={handleSubmit}>
        <PageTitle
          title={docType.name || t('entity.taxonomy.docType._singular')}
        />

        <FormWrapper>
          <FormWrapper.Single>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              {t('entity.offers.product.name._')}
            </Typography.Paragraph>
            <Collapse
              activeKey={activeKeys}
              onChange={() => setActiveKey(activeKeys ? null : 'name')}
            >
              <CollapsePanel key="name" header={value.name}>
                <FormWrapper.Input
                  label={t('entity.taxonomy.docType.name.ro')}
                  props={{
                    ...input('localizedName.ro'),
                    disabled: value.deleted || !canEdit,
                  }}
                />
                <FormWrapper.Input
                  label={t('entity.taxonomy.docType.name.en')}
                  props={{
                    ...input('localizedName.en'),
                    disabled: value.deleted || !canEdit,
                  }}
                />
                <FormWrapper.Input
                  label={t('entity.taxonomy.docType.name.de')}
                  props={{
                    ...input('localizedName.de'),
                    disabled: value.deleted || !canEdit,
                  }}
                />
              </CollapsePanel>
            </Collapse>

            <FormWrapper.Input
              label={t('entity.taxonomy.docType.description')}
              props={{ ...input('description'), disabled: deleted || !canEdit }}
            />

            <FormWrapper.Input
              label={t('entity.taxonomy.docType.code')}
              props={{ ...input('code'), disabled: deleted || !canEdit }}
            />

            <FormWrapper.Switch
              label={t('entity.taxonomy.docType.docRequired')}
              props={{
                ...checkbox('docRequired'),
                disabled: !canEdit,
                onChange: (val) => {
                  setValue('docRequired', val);
                },
              }}
            />

            <FormWrapper.Switch
              label={t('entity.taxonomy.docType.hasExpirationDate')}
              props={{
                ...checkbox('hasExpirationDate'),
                onChange: (v1) => {
                  setValue('hasExpirationDate', v1);
                  if (!v1) {
                    setValue('durationType', null);
                    setValue('duration', null);
                    setValue('dateChangeable', false);
                  }
                },
                disabled: deleted || !canEdit,
              }}
            />
            <Row gutter={8}>
              <Col span={12}>
                <FormWrapper.Select
                  label={t('entity.taxonomy.docType.durationType._')}
                  props={{
                    options: DURATION_TYPES,
                    ...select('durationType'),
                    disabled:
                      value.deleted || !value.hasExpirationDate || !canEdit,
                    onChange: (v1) => {
                      setValue('durationType', v1);
                      if (!v1) {
                        setValue('duration', null);
                        setValue('dateChangeable', false);
                      }
                    },
                    allowClear: true,
                    labelProp: (val) =>
                      t(`entity.taxonomy.docType.durationType.${val.id}`),
                  }}
                />
              </Col>
              <Col span={12}>
                <FormWrapper.Number
                  label={t('entity.taxonomy.docType.duration')}
                  props={{
                    ...input('duration'),
                    min: 1,
                    disabled:
                      value.deleted ||
                      !value.hasExpirationDate ||
                      !value.durationType ||
                      !canEdit,
                  }}
                />
              </Col>
            </Row>
            <FormWrapper.Switch
              label={t('entity.taxonomy.docType.dateChangeable')}
              props={{
                ...checkbox('dateChangeable'),
                disabled:
                  deleted ||
                  !value.hasExpirationDate ||
                  !value.durationType ||
                  !canEdit,
              }}
            />

            <Space style={{ paddingTop: '1rem' }}>
              {!deleted && canEdit && <SaveButton />}
              <CancelButton onCancel={onCancel} />
            </Space>
          </FormWrapper.Single>
        </FormWrapper>
      </form>
    </>
  );
};
export default EditDocTypeForm;
